import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Mail, Send, Loader2, CheckCircle2, AlertTriangle, Lock, Clock, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { api } from '../utils/api';

const MAX_SUBJECT = 140;
const MAX_BODY = 50000;

const Campaigns = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [subCount, setSubCount] = useState(null);
  const [cooldown, setCooldown] = useState({ can_send: true, seconds_until_next: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isPro = user?.subscription?.is_pro;

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (!isPro) return;
    api.getMySubscribers?.({ limit: 1 }).catch(() => null);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribers?limit=1`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => setSubCount(d.total_all ?? 0)).catch(() => setSubCount(0));
    api.getCampaignCooldown().then(setCooldown).catch(() => {});
    api.getCampaigns().then((d) => setCampaigns(d.campaigns || [])).catch(() => {});
  }, [user, navigate, isPro]);

  const cooldownHrs = useMemo(
    () => Math.ceil(cooldown.seconds_until_next / 3600),
    [cooldown.seconds_until_next]
  );

  if (!user) return null;

  if (!isPro) {
    return (
      <div className="min-h-screen bg-background" data-testid="campaigns-pro-gate">
        <Nav />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/15 text-amber-600 flex items-center justify-center mx-auto mb-5">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mb-2">Email your subscribers</h1>
          <p className="text-muted-foreground mb-6">
            Reach every confirmed subscriber with one email — direct, no algorithm in between.
            Pro feature.
          </p>
          <Link to="/pricing">
            <Button data-testid="campaigns-upgrade-btn">Unlock with Pro</Button>
          </Link>
        </main>
      </div>
    );
  }

  const canSend = cooldown.can_send && (subCount || 0) > 0 && subject.trim() && body.trim();

  const handleSend = async () => {
    setSending(true);
    setConfirmOpen(false);
    try {
      const res = await api.sendCampaign(subject.trim(), body.trim());
      toast.success(res.message || 'Campaign sending');
      setSubject('');
      setBody('');
      // Refresh cooldown + history
      const [c, h] = await Promise.all([
        api.getCampaignCooldown(),
        api.getCampaigns(),
      ]);
      setCooldown(c);
      setCampaigns(h.campaigns || []);
    } catch (e) {
      toast.error(e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="campaigns-page">
      <Helmet><title>Email subscribers — LinkMonkey</title></Helmet>
      <Nav />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Pro</p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-1">Email your subscribers</h1>
          <p className="text-muted-foreground text-sm">One plain-text email to every confirmed subscriber. One send per 24 hours, up to 5,000 recipients.</p>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              <Users className="w-3.5 h-3.5" /> Confirmed subscribers
            </div>
            <p className="text-2xl font-semibold tracking-tight" data-testid="sub-count">
              {subCount === null ? '—' : subCount.toLocaleString()}
            </p>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              <Clock className="w-3.5 h-3.5" /> Next send
            </div>
            <p className="text-2xl font-semibold tracking-tight" data-testid="cooldown">
              {cooldown.can_send ? 'Ready' : `${cooldownHrs}h`}
            </p>
          </Card>
        </div>

        {/* Composer */}
        <Card className="p-6 space-y-4" data-testid="composer-card">
          <div>
            <label className="text-sm font-medium block mb-1.5">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value.slice(0, MAX_SUBJECT))}
              placeholder="e.g. New EP out today 🎧"
              data-testid="subject-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">{subject.length}/{MAX_SUBJECT}</p>
          </div>
          <div>
            <label className="text-sm font-medium block mb-1.5">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, MAX_BODY))}
              placeholder={`Hey,\n\nQuick note from me — I just dropped...\n\nThe link is on my profile. Cheers!`}
              rows={12}
              className="w-full text-sm font-mono p-3 rounded-md border border-input bg-background resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="body-textarea"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Plain text. URLs auto-link. Every email includes a one-click unsubscribe footer. {body.length.toLocaleString()}/{MAX_BODY.toLocaleString()}
            </p>
          </div>

          {!cooldown.can_send && (
            <div className="flex gap-2 p-3 rounded-lg bg-amber-500/[0.06] border border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>You can send your next campaign in about <strong>{cooldownHrs} hour{cooldownHrs !== 1 ? 's' : ''}</strong>.</span>
            </div>
          )}

          {(subCount || 0) === 0 && (
            <div className="flex gap-2 p-3 rounded-lg bg-muted/30 border border-border text-sm text-muted-foreground">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>You don't have any confirmed subscribers yet. Enable the email signup widget in Settings to start collecting them.</span>
            </div>
          )}

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">From: <strong>{user.name || user.username}</strong> &lt;hello@linkmonkey.io&gt; · Reply-To: <strong>{user.email}</strong></p>
            <Button onClick={() => setConfirmOpen(true)} disabled={!canSend || sending} data-testid="send-campaign-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Review & send
            </Button>
          </div>
        </Card>

        {/* History */}
        {campaigns.length > 0 && (
          <Card className="p-6" data-testid="campaign-history">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-muted-foreground" /> Past campaigns
            </h2>
            <div className="space-y-2">
              {campaigns.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/20 text-sm" data-testid={`campaign-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{c.subject}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(c.sent_at).toLocaleString('en-IE', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant={c.status === 'complete' ? 'default' : 'secondary'} className="text-[10px]">
                      {c.status === 'complete' ? <><CheckCircle2 className="w-3 h-3 mr-1" />sent</> : c.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {c.sent_count}/{c.recipient_count}
                      {c.failed_count > 0 && <span className="text-amber-600"> · {c.failed_count} failed</span>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>

      {/* Confirm modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmOpen(false)} data-testid="confirm-overlay">
          <Card className="p-6 max-w-md w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-semibold tracking-tight">Send to {(subCount || 0).toLocaleString()} subscriber{subCount === 1 ? '' : 's'}?</h2>
            <div className="rounded-lg bg-muted/30 border border-border p-3 text-sm space-y-2">
              <div><span className="text-muted-foreground text-xs">Subject</span><p className="font-medium">{subject}</p></div>
              <div><span className="text-muted-foreground text-xs">Preview</span><p className="whitespace-pre-wrap line-clamp-4 text-[13px]">{body}</p></div>
            </div>
            <p className="text-xs text-muted-foreground">This send counts against your 1-per-24h limit. You won't be able to send another until tomorrow.</p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="confirm-cancel">Cancel</Button>
              <Button onClick={handleSend} disabled={sending} data-testid="confirm-send">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send now
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const Nav = () => (
  <nav className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-10">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
      <Link to="/settings" className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Back to settings
      </Link>
    </div>
  </nav>
);

export default Campaigns;
